/**
 * Payment request/response schemas (plan 04 §3.1). Shared by the /payments Edge
 * handler and the typed client caller. Money is integer minor units (MKD × 100).
 */
import { z } from 'zod';

export const DiscountInputSchema = z.union([
  z.object({ type: z.literal('promo_code'), code: z.string().min(1) }),
  z.object({ type: z.literal('gift_card'), code: z.string().min(1) }),
  z.object({ type: z.literal('discount_id'), id: z.string().uuid() }),
]);

export const TenderSchema = z.object({
  method: z.enum(['cash', 'card', 'bank_transfer', 'voucher', 'gift_card', 'online']),
  amount: z.number().int().positive(),
});

const LineItemSchema = z.object({
  name: z.string().min(1),
  quantity: z.number().positive(),
  unit_price: z.number().int().nonnegative(),
  tax_rate_code: z.enum(['A', 'B', 'C', 'EXEMPT']),
  total: z.number().int().nonnegative(),
  product_ref: z.string().optional(),
});

export const RecordPaymentRequestSchema = z.object({
  tenant_id: z.string().uuid(),
  location_id: z.string().uuid(),
  member_id: z.string().uuid().nullable(),
  kind: z.enum(['membership', 'retail', 'pt_session', 'class_pack', 'day_pass', 'other']),
  tenders: z.array(TenderSchema).min(1),
  gross_amount: z.number().int().positive(),
  discount_input: DiscountInputSchema.optional(),
  membership_plan_id: z.string().uuid().optional(),
  sale_id: z.string().uuid().optional(),
  recorded_by_staff_id: z.string().uuid(),
  issued_at: z.string().datetime(),
  client_event_id: z.string().uuid().optional(),
  fiscal_device_id: z.string().uuid().optional(),
  fiscalize: z.boolean().default(true),
  notes: z.string().max(500).optional(),
  line_items: z.array(LineItemSchema).min(1),
});
export type RecordPaymentRequest = z.infer<typeof RecordPaymentRequestSchema>;

export const RecordPaymentResponseSchema = z.object({
  payment_id: z.string().uuid(),
  status: z.enum(['completed', 'pending']),
  fiscal_status: z.enum(['issued', 'failed_retriable', 'failed', 'pending', 'not_required']),
  fiscal_receipt_id: z.string().uuid().nullable(),
  device_receipt_number: z.string().nullable(),
  fiscal_qr_payload: z.string().nullable(),
  amount_charged: z.number().int(),
  discount_applied: z.number().int(),
  membership_id: z.string().uuid().nullable(),
  created_at: z.string(),
});
export type RecordPaymentResponse = z.infer<typeof RecordPaymentResponseSchema>;

export const RefundPaymentRequestSchema = z.object({
  tenant_id: z.string().uuid(),
  reason: z.string().min(1).max(500),
  amount: z.number().int().positive(),
  recorded_by_staff_id: z.string().uuid(),
});
export type RefundPaymentRequest = z.infer<typeof RefundPaymentRequestSchema>;
